import {AsyncPipe} from '@angular/common';
import {
    AfterViewChecked,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {VideoAsset} from '@app/asset';
import {PresentingState, ReviewState, Slot} from "@app/review/models";
import {IResizable} from '@app/review/resizable';
import {PlaybackState, ReviewVideoService, Source, VideoState,} from '@app/review/review-video.service';
import {ReviewService} from '@app/review/review.service';
import {TimeScale} from "@app/review/timescale";
import {Matrix, Rect, Size} from '@app/shared/euclid';
import {VideoProcessingComponent} from '@app/shared/video-processing/video-processing.component';
import {ViewerToolsService} from '@app/viewer/viewer-tools.service';
import {MessageType} from '@app/websocket/websocket.service';

import * as Sentry from "@sentry/angular";
import {combineLatestWith, defer, fromEvent, map, Observable} from 'rxjs';
import {filter, startWith} from 'rxjs/operators';


class ServiceStateVM {
    reviewState: ReviewState;
    presentingState: PresentingState;
}

@Component({
    selector: 'frog-review-video',
    imports: [AsyncPipe, VideoProcessingComponent, MatProgressSpinner],
    templateUrl: './review-video.component.html',
    styleUrl: './review-video.component.scss',
})
export class ReviewVideoComponent implements OnInit, AfterViewChecked, OnDestroy, OnChanges, IResizable {
    @Input() asset: VideoAsset;
    // Which half of the compare-parameterized services (review-video.service.ts) this instance reads/writes -
    // Slot.Primary when there's a single asset or this is the left pane, Slot.Compare for the right pane.
    @Input() slot: Slot = Slot.Primary;
    // False for the passive pane while comparing - it's a pure display surface (no timeline, no click-to-
    // play, no bounds reporting to the Stage overlay). See docs/design/review-compare-assets.md §3.4.
    @Input() isActive: boolean = true;
    @ViewChild('video') videoElement: ElementRef<HTMLVideoElement>;

    public serviceStateVM$: Observable<ServiceStateVM>;
    public videoState$: Observable<VideoState>;
    public playbackState$: Observable<PlaybackState>;
    public failedAssetIds$: Observable<Set<string>>;

    public xform: Matrix;
    public bounds: Rect;

    public loading: boolean;

    private video: HTMLVideoElement;
    private playHeartbeatTimer: NodeJS.Timeout;

    private _canPause: boolean;
    private _pauseRequested: boolean;
    private _resizeObserver?: ResizeObserver;
    private _resizeTimeout?: ReturnType<typeof setTimeout>;
    private _timeScale: TimeScale;
    private _queuedFrame: number;
    private _lastFrame: number = -1;
    private readonly _heartbeatTimeout: number = 300;

    constructor(
        private reviewService: ReviewService,  // Send ws notifications, presentation state, set asset bounds
        private reviewVideoService: ReviewVideoService,
        private viewerToolsService: ViewerToolsService,
        private host: ElementRef,
        private zone: NgZone,
    ) {
        this.xform = Matrix.Identity();

        this._canPause = true;
        this._pauseRequested = false;
        this._timeScale = null;
        this._queuedFrame = -1;
    }

    ngOnInit() {
        this.failedAssetIds$ = this.reviewService.failedAssetIds$;

        this.serviceStateVM$ = this.reviewService.reviewState$.pipe(
            combineLatestWith(this.reviewService.presenterState$),
            map(([reviewState, presentingState]) => {
                return { reviewState, presentingState } as ServiceStateVM;
            }),
        );

        // Seeded (at subscribe time) with this slot's current state - see ReviewVideoService.getVideoState().
        this.videoState$ = defer(() => this.reviewVideoService.videoState$.pipe(
            filter((videoState) => {
                return videoState.source !== Source.Video && videoState.slot === this.slot;
            }),
            startWith(this.reviewVideoService.getVideoState(this.slot)),
        )).pipe(
            map((videoState) => {
                // The passive pane never plays audio of its own, regardless of the shared mute toggle -
                // only the active pane's chrome controls mute/volume (§3.4).
                this.applyMuteState(videoState.isMuted || !this.isActive);
                // Nor does it loop on its own - it follows the active pane's loop point via mirrored frames
                // (see onFrame()), so a longer passive Asset wraps when the active one does.
                this.applyLoopState(videoState.isLooping && this.isActive);
                this.applyVolume(videoState.volume);

                return videoState;
            }),
        );

        this.playbackState$ = defer(() => this.reviewVideoService.playbackState$.pipe(
            filter((playbackState) => {
                return playbackState.source !== Source.Video && playbackState.slot === this.slot;
            }),
            startWith(this.reviewVideoService.getPlaybackState(this.slot)),
        )).pipe(
            map((playbackState) => {
                this._timeScale = new TimeScale(playbackState.frameCount, playbackState.frameRate);
                this.applyPlayState(playbackState.isPlaying);

                if (Number.isNaN(playbackState.frame)) {
                    Sentry.captureMessage("PlaybackState frame was NaN", {
                        extra: {
                            playbackState
                        }
                    });
                }
                this.applyFrame(playbackState.frame);

                if (playbackState.source === Source.Timeline && playbackState.isPlaying) {
                    this.startPlayHeartbeat();
                }

                return playbackState;
            }),
        );

        this._resizeObserver = new ResizeObserver(() => {
            // ResizeObserver can deliver synchronously mid-digest (e.g. off a
            // forced layout read elsewhere in the same tick), which races
            // Angular's checkNoChanges pass and throws NG0100 on the
            // bounds-derived [style.width.px]/[style.height.px] bindings.
            // Defer to the next macrotask so the update lands in its own
            // change-detection run.
            clearTimeout(this._resizeTimeout);
            this._resizeTimeout = setTimeout(() => this.resize());
        });
    }

    ngAfterViewChecked() {
        if (this.video) {
            return;
        }

        if (!this.videoElement) {
            return;
        }

        this.video = this.videoElement.nativeElement;
        this.video.requestVideoFrameCallback(this.onFrame.bind(this));
        fromEvent(this.video, 'progress').subscribe((event) => {
            if (!this.isActive) {
                // buffered$ isn't slot-tagged (it's a transient UI indicator, not synced state) - only the
                // active pane's own buffering should drive the one shared timeline's buffer bar.
                return;
            }
            this.reviewVideoService.setBuffered(this.video.buffered);
        });
        fromEvent(this.video, 'loadeddata').subscribe((event) => {
            this.loading = false;
        });

        this._resizeObserver.observe(this.host.nativeElement);

        if (this._queuedFrame >= 0) {
            const frame = this._queuedFrame;
            this._queuedFrame = -1;
            this.applyFrame(frame);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // Only a genuinely new Asset needs to show the spinner again and wait on a fresh <video> load - the
        // `loading` flag only ever clears via that element's 'loadeddata' event (ngAfterViewChecked), which
        // won't fire again just because `isActive`/`slot` flipped (e.g. clicking a compare pane to activate
        // it, §3.4) while `asset` itself is unchanged. Resetting `loading` on every input change left it
        // stuck true forever in that case, since nothing was left to clear it (same bug fixed for
        // ReviewImageComponent - see its ngOnChanges()).
        const assetChange = changes.asset;
        if (!assetChange || assetChange.previousValue?.id === assetChange.currentValue?.id) {
            return;
        }

        this.loading = true;
    }

    ngOnDestroy() {
        this._resizeObserver.disconnect();
        clearTimeout(this._resizeTimeout);
        clearInterval(this.playHeartbeatTimer);
    }

    // Apply State
    public applyFrame(frame: number) {
        if (!this.video) {
            this._queuedFrame = frame;
            return;
        }

        if (!this.isActive && frame >= this._timeScale.frameCount) {
            // A shorter passive Asset holds on its last frame until the active one wraps, rather than
            // seeking past its end (which leaves it 'ended', so the next play() restarts it from 0).
            frame = Math.max(this._timeScale.frameCount - 1, 0);
        }

        const time = this._timeScale.frameToTime(frame);
        if (!Number.isFinite(time)) {
            Sentry.captureMessage("Tried to set video time to a non-finite value", {
                extra: {
                    value: time,
                    frame: frame,
                    frameRate: this._timeScale.frameRate
                }
            });
            return;
        }

        this.video.currentTime = time;
    }

    public applyPlayState(isPlaying: boolean) {
        clearInterval(this.playHeartbeatTimer);
        if (!this.video) {
            return;
        }

        if (isPlaying) {
            this._canPause = false;
            this.video.play()
                .then(() => {
                    this._canPause = true;
                    if (this._pauseRequested) {
                        this.video.pause();
                        this._pauseRequested = false;
                    }
                })
                .catch(error => {
                    // Ignore interruption errors
                    // https://developer.chrome.com/blog/play-request-was-interrupted
                });
        } else {
            if (this._canPause) {
                this.video.pause();
            }
            else {
                this._pauseRequested = true;
            }
        }
    }

    public applyMuteState(isMuted: boolean) {
        if (!this.video) {
            return;
        }

        this.video.muted = isMuted;
    }

    public applyLoopState(isLooping: boolean) {
        if (!this.video) {
            return;
        }

        this.video.loop = isLooping;
    }

    public applyVolume(volume: number) {
        if (!this.video) {
            return;
        }

        this.video.volume = volume;
    }

    applyReviewState(reviewState: any) {
        this.resize();
    }

    // Set state
    public play() {
        if (!this.isActive) {
            // Passive-pane playback only ever moves via lock-mirroring (§3.5) - it never reports its own
            // heartbeat, which would otherwise fight with the mirrored value.
            return;
        }
        // Heartbeat
        this.startPlayHeartbeat();
    }

    public pause() {
        // Clear heartbeat
        clearInterval(this.playHeartbeatTimer);
    }

    // Event Handlers
    public clickHandler(presentingState: PresentingState) {
        if (presentingState === PresentingState.Other) {
            return;
        }

        if (!this.isActive) {
            // The passive pane has no interactive surface - direct manipulation only ever targets the
            // active slot (§3.4).
            return;
        }

        this.reviewVideoService.play(Source.All, this.video.paused, this.slot);

        const data = {
            [this.fieldName('IsPlaying')]: !this.video.paused,
            [this.fieldName('Frame')]: this._timeScale.timeToFrame(this.video.currentTime),
        };
        this.reviewService.send(data, MessageType.ReviewState);
    }

    // The outgoing ReviewState field name for this pane's slot - 'Frame'/'IsPlaying' for Primary,
    // 'CompareFrame'/'CompareIsPlaying' for Compare (see docs/design/review-compare-assets.md §3.2).
    private fieldName(baseName: string): string {
        return this.slot === Slot.Compare ? `Compare${baseName}` : baseName;
    }

    public resize() {
        if (!this.asset) {
            return;
        }

        this.bounds = this.host.nativeElement.getBoundingClientRect() as Rect;
        this.xform = new Matrix([
            [this.asset.width, 0, 0],
            [0, this.asset.height, 0],
            [0, 0, 1],
        ]);

        const size = this.xform.rect.fit(this.bounds.width, this.bounds.height);
        const scale = size.width / this.asset.width;
        this.center(scale);

        this.notifyBounds();
    }

    private notifyBounds() {
        if (!this.video) {
            return;
        }

        if (!this.isActive) {
            // The Stage overlay (annotations/cursors) only ever tracks the active slot's Asset (§3.4) - a
            // passive pane reporting its own bounds would make Stage draw into the wrong pane.
            return;
        }

        const bounds = Rect.fromDomRect(this.video.getBoundingClientRect());
        const relativeBounds = Rect.fromDomRect(this.video.getBoundingClientRect());
        relativeBounds.x = this.video.offsetLeft;
        relativeBounds.y = this.video.offsetTop;

        const xformRect = this.xform.rect;
        const scale = this.asset.width / xformRect.width;
        const assetSize = new Size(this.asset.width, this.asset.height);

        this.reviewService.setAssetBounds({bounds, relativeBounds, scale, assetSize, assetId: this.asset.id, slot: this.slot});
    }

    // Transformations
    center(scale: number = 1.0) {
        this.xform = new Matrix([
            [this.asset.width, 0, 0],
            [0, this.asset.height, 0],
            [0, 0, 1],
        ]);
        this.scale(scale, scale);
        const x: number = this.asset.width / 2 - this.xform.elements[0][0] / 2;
        const y: number = this.asset.height / 2 - this.xform.elements[1][1] / 2;
        this.translate(x, y);
    }

    translate(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [x, y, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
    }

    scale(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [x, 0, 0],
            [0, y, 0],
            [0, 0, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
    }

    private onFrame(now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) {
        this.zone.run(() => {
            if (!this.video.paused) {
                const frame = this._timeScale.timeToFrame(metadata.mediaTime);
                this.reviewVideoService.setFrame(frame, Source.Video, this.slot);

                if (this.isActive && frame < this._lastFrame) {
                    // The active pane just looped - push its frame now rather than on the next heartbeat
                    // tick, so a locked passive pane (which doesn't loop natively) wraps with it.
                    this.startPlayHeartbeat();
                    this.sendHeartbeat();
                }
                this._lastFrame = frame;
            }
        });
        this.video.requestVideoFrameCallback(this.onFrame.bind(this));
    }

    emitTools() {
        const viewerTools = [];

        this.viewerToolsService.setTools(viewerTools);
    }

    startPlayHeartbeat() {
        clearInterval(this.playHeartbeatTimer);

        this.playHeartbeatTimer = setInterval(() => this.sendHeartbeat(), this._heartbeatTimeout);
    }

    private sendHeartbeat() {
        const data = {
            SilentChange: true,
            [this.fieldName('Frame')]: this._timeScale.timeToFrame(this.video.currentTime),
        };
        this.reviewService.send(data, MessageType.ReviewState);
    }
}
